import React, { forwardRef, PropsWithChildren, useState } from 'react';
import { IShadowProps, useShadowCss } from '../../fundamentals/shadows/shadows';
import { ISheetBaseProps, ISheetProps } from './Sheet';
import styled from 'styled-components';
import { SIZES } from '../../fundamentals/sizes/sizes';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { IGutterProps, useGutterCss } from '../../fundamentals/spaces/space';
import { P } from '../Texts/Texts';
import { FONT_WEIGHTS } from '../../fundamentals/typography/typography';
import { BackButton } from '../BackNextButton/BackNextButton';
import { CloseButton } from '../CloseButton/CloseButton';
import { useSwipeable } from 'react-swipeable';
import merge from 'lodash.merge';
import { BaseDiv } from '../../common/BaseElements';

interface ISheetBodyProps extends IShadowProps, Pick<ISheetProps, 'onHide'>, ISheetBaseProps {}

const SHEET_BORDER_RADIUS_SIZE = SIZES.XX_SMALL;

export const MIN_SHEET_HEIGHT = `50vh`;

const SheetBodyContainer = styled(BaseDiv)<ISheetBodyProps & IGutterProps>`
  border-radius: ${({ theme }) => `${theme.borderRadius[SHEET_BORDER_RADIUS_SIZE]} ${theme.borderRadius[SHEET_BORDER_RADIUS_SIZE]} 0 0`};
  background-color: ${({ theme }) => theme.menuColours.background};

  max-height: ${({ theme, peek }) => (peek ? MIN_SHEET_HEIGHT : `calc(100vh - ${theme.spaces[64]})`)};

  display: flex;
  flex-direction: column;

  ${useShadowCss()};
  ${useGutterCss({ top: 8 })};
`;

const Handle = styled(BaseDiv)<IGutterProps>`
  margin-right: auto;
  margin-left: auto;

  width: ${({ theme }) => theme.sizes[SIZES.LARGE]};
  height: ${({ theme }) => theme.spaces[4]};
  background: ${({ theme }) => theme.sheetColours.handle.background};
  border-radius: ${({ theme }) => theme.spaces[2]};
`;

const SheetHeader = styled(BaseDiv)<IGutterProps>`
  ${useGutterCss()};
  width: 100%;
  display: inline-flex;
`;

const Title = styled(P)`
  width: 100%;
`;

const SheetContent = styled(BaseDiv)<IGutterProps>`
  ${useGutterCss()};

  overflow-y: auto;
`;

const SwipeableArea = styled(BaseDiv)<IGutterProps>`
  ${useGutterCss({ right: 16, left: 16 })};
`;

const SWIPE_CONFIG = {
  trackMouse: true
};

export const SheetBody = forwardRef<HTMLDivElement, PropsWithChildren<ISheetBodyProps>>(
  ({ children, title, closable, onHide, onBackButtonClick, showBackButton, swipeableConfig, showHandle = true, peek = true, ...rest }, ref) => {
    const { generateTestId } = useTestId(rest, 'Sheet');

    const showHeader = showBackButton || title || closable;
    const [full, setFull] = useState(!peek);
    const config = merge(SWIPE_CONFIG, swipeableConfig);

    const swipeableHandlers = useSwipeable({
      onSwipedUp: () => !full && setFull(true),
      onSwipedDown: () => full && setFull(false),
      ...config
    });

    return (
      <SheetBodyContainer peek={!full} {...generateTestId('body')} {...rest} ref={ref}>
        <SwipeableArea {...swipeableHandlers} {...generateTestId('swipeable')}>
          {showHandle && <Handle {...generateTestId('handle')} />}
          {showHeader && (
            <SheetHeader gutterTop={16} gutterBottom={8} {...generateTestId('header')}>
              {showBackButton && (
                <BackButton
                  constantWidth
                  showText={false}
                  variant='linkDark'
                  onClick={onBackButtonClick}
                  // TODO: revisit when Buttons(v2) are finalized in Figma
                  size={SIZES.MEDIUM}
                  {...generateTestId('back')}
                />
              )}
              {title && (
                <Title
                  textAlign='center'
                  fontWeightCss={FONT_WEIGHTS.SEMI_BOLD}
                  // TODO: revisit when Buttons(v2) are finalized in Figma
                  gutterRight={showBackButton && !closable ? 40 : 16}
                  // TODO: revisit when Buttons(v2) are finalized in Figma
                  gutterLeft={!showBackButton && closable ? 40 : 16}
                  gutterBottom={false}
                  {...generateTestId('title')}
                >
                  {title}
                </Title>
              )}
              {closable && <CloseButton onClick={onHide} size={SIZES.X_SMALL} {...generateTestId('close')} />}
            </SheetHeader>
          )}
        </SwipeableArea>
        <SheetContent {...generateTestId('content')}>{children}</SheetContent>
      </SheetBodyContainer>
    );
  }
);
